import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { IssueViewContext } from "#app/components/issue-view";
import { EntityDetailContext } from "#app/components/entity/entity-detail-context";
import {
  type DetailKind,
  useDetailStackAnnounce,
} from "#app/components/detail-view/detail-stack";
import { useAppData } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import type { EntityKind } from "#app/lib/entity-visuals";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useViewerCanOpenProfiles } from "#app/lib/hooks/use-permission";
import { AnalyticsContext, MemberEvent, useAnalytics } from "#app/lib/analytics";

// Lives in app-providers.tsx, above every overlay host (so portaled overlay
// content can call it), and renders NO dialog inline — split-context rule.
// EntityDetailDialogs reads `active`. Routing: `issue` owns a dedicated
// overlay and delegates to that overlay's provider; `team` has no overlay at
// all — its detail IS the team page (`/team/[teamId]`), so it navigates;
// every other kind sets `active` for the dialog host. That overlay delegation
// is why the issue provider keeps its dialogs in a separate
// `*-view-dialogs.tsx`: this module sits inside it in the provider stack, so
// importing its surfaces here would close a cycle.

export type ActiveEntityDetail = {
  kind: Exclude<EntityKind, "issue" | "team">;
  id: string;
};


export type OpenEntityOptions = {
  /** The surface the open came from, for analytics (`member.open_profile`
   *  `data.via`): a stable snake_case name such as "members_table". */
  via?: string;
};
export interface EntityDetailContextValue {
  active: ActiveEntityDetail | null;
  openEntity: (kind: EntityKind, id: string, options?: OpenEntityOptions) => void;
  closeEntity: () => void;
  /**
   * Whether `openEntity(kind, id)` has somewhere to go, asked up front so a
   * chip renders inert instead of opening nothing. False for a team outside
   * the viewer's loaded team list — the same object-level guard the team page
   * applies (a team missing from workspace data is one the viewer can't see,
   * or one that doesn't exist) — and for another member's profile when the
   * viewer is a Guest (see `viewerCanOpenProfiles`). A Guest may still open
   * their own. Every other kind is always openable. For `user` a false answer
   * also withholds the hover card: the card is a profile excerpt (contact
   * details, teams), not a mere link to one.
   */
  canOpenEntity: (kind: EntityKind, id: string) => boolean;
  /**
   * Whether member profiles are available to this viewer at all
   * (`useViewerCanOpenProfiles`, republished here so chip-level code needs
   * one context). Pool-level affordances that carry no member id read this;
   * per-member gates read `canOpenEntity("user", id)`.
   */
  viewerCanOpenProfiles: boolean;
  /**
   * Registers an open overlay to close before an entity detail presents
   * OUTSIDE the modal stack — the member profile island (inline in the
   * shell's right slot, so any modal's fixed z-50 backdrop buries it inert)
   * and the team page (a navigation the overlay would sit over). Returns the
   * unregister cleanup. Overlays register through `useYieldToEntityDetail`,
   * not this directly.
   */
  registerDetailYield: (onClose: () => void) => () => void;
  /** Inside `ContainEntityDetail`: opens that would leave the surface are
   *  withheld, so previews drop their open actions. */
  contained: boolean;
}

/** The kinds `openEntity` hands to a dedicated detail overlay. */
const STACKED_KINDS: ReadonlySet<EntityKind> = new Set<EntityKind>(["issue"]);

function isDetailKind(kind: EntityKind): kind is DetailKind {
  return STACKED_KINDS.has(kind);
}

export function EntityDetailProvider({ children }: { children: React.ReactNode }) {
  // The RAW overlay contexts, not their hooks: the hooks announce an open to
  // the detail stack from where they are called, and this provider sits above
  // every drawer — it would announce "outside" for a chip inside one. The
  // announcing happens in `useEntityDetail` instead, at the chip.
  const issueView = useContext(IssueViewContext);
  if (!issueView) {
    throw new Error("EntityDetailProvider must sit below IssueViewProvider");
  }
  const { openIssueById } = issueView;
  const { teamMap } = useWorkspaceData();
  const { currentUser } = useAppData();
  const { push } = useAppNavigate();
  const { track } = useAnalytics();
  const [active, setActive] = useState<ActiveEntityDetail | null>(null);

  const viewerCanOpenProfiles = useViewerCanOpenProfiles();
  const viewerId = currentUser?.userId ?? null;
  const canOpenEntity = useCallback(
    (kind: EntityKind, id: string) => {
      if (kind === "team") return teamMap.has(id);
      if (kind === "user") return viewerCanOpenProfiles || id === viewerId;
      return true;
    },
    [teamMap, viewerCanOpenProfiles, viewerId],
  );

  const yieldHandlers = useRef(new Set<() => void>());
  const registerDetailYield = useCallback((onClose: () => void) => {
    yieldHandlers.current.add(onClose);
    return () => {
      yieldHandlers.current.delete(onClose);
    };
  }, []);
  const closeYieldingOverlays = useCallback(() => {
    // Snapshot: a handler's close unregisters it mid-iteration.
    for (const close of [...yieldHandlers.current]) close();
  }, []);

  const openEntity = useCallback(
    (kind: EntityKind, id: string, options?: OpenEntityOptions) => {
      if (kind === "issue") {
        openIssueById(id, { source: options?.via ?? "entity_link" });
        return;
      }
      if (kind === "team") {
        // The route's own guard would answer Access Denied for a team outside
        // the viewer's list; a no-op here keeps a stale caller from bouncing
        // the user off their current screen for nothing.
        if (teamMap.has(id)) {
          closeYieldingOverlays();
          push(`/team/${id}`);
        }
        return;
      }
      if (kind === "user") {
        // A Guest has no profile to open for anyone but themself; a stale or
        // hand-built caller (org-chart node, search row) no-ops instead of
        // sliding out an island the viewer is not meant to see.
        if (!canOpenEntity(kind, id)) return;
        // The profile presents as the shell's right island, which no stacking
        // order can lift over a modal — open overlays yield first. The other
        // `active` kinds present as dialogs in the modal stack and leave their
        // host open.
        closeYieldingOverlays();
        track({
          context: AnalyticsContext.Member,
          event: MemberEvent.OpenProfile,
          data: { via: options?.via ?? "unknown", self: id === viewerId },
        });
      }
      setActive({ kind, id });
    },
    [
      canOpenEntity,
      closeYieldingOverlays,
      openIssueById,
      push,
      teamMap,
      track,
      viewerId,
    ],
  );

  const closeEntity = useCallback(() => setActive(null), []);

  const value = useMemo(
    () => ({
      active,
      openEntity,
      closeEntity,
      canOpenEntity,
      viewerCanOpenProfiles,
      registerDetailYield,
      contained: false,
    }),
    [
      active,
      openEntity,
      closeEntity,
      canOpenEntity,
      viewerCanOpenProfiles,
      registerDetailYield,
    ],
  );

  return (
    <EntityDetailContext.Provider value={value}>
      {children}
    </EntityDetailContext.Provider>
  );
}

/** Wraps `openEntity` so an open of a stacked kind announces itself to the
 *  detail stack from the CALLER's position — inside a drawer it pushes over
 *  it, elsewhere it replaces what is open. */
function useAnnouncedEntityDetail(
  ctx: EntityDetailContextValue | null,
): EntityDetailContextValue | null {
  const announce = useDetailStackAnnounce();
  return useMemo(() => {
    if (!ctx) return null;
    return {
      ...ctx,
      openEntity: (kind: EntityKind, id: string, options?: OpenEntityOptions) => {
        if (isDetailKind(kind)) announce(kind);
        ctx.openEntity(kind, id, options);
      },
    };
  }, [ctx, announce]);
}

export function useEntityDetail(): EntityDetailContextValue {
  const ctx = useAnnouncedEntityDetail(useContext(EntityDetailContext));
  if (!ctx) {
    throw new Error("useEntityDetail must be used within EntityDetailProvider");
  }
  return ctx;
}

// Team and issue replace the screen; a profile closes every yielding overlay.
const LEAVING_KINDS = new Set<EntityKind>(["team", "issue", "user"]);

/**
 * For a surface that must not be left, like an unsaved draft: entity opens that
 * would navigate away or close it become no-ops, and team and issue chips read
 * as unopenable. Hover cards stay, minus their open actions.
 */
export function ContainEntityDetail({ children }: { children: React.ReactNode }) {
  const ctx = useContext(EntityDetailContext);
  const value = useMemo<EntityDetailContextValue | null>(
    () =>
      ctx && {
        ...ctx,
        contained: true,
        // `user` keeps its answer: false would also withhold the profile card.
        canOpenEntity: (kind, id) =>
          kind === "team" || kind === "issue" ? false : ctx.canOpenEntity(kind, id),
        openEntity: (kind, id, options) => {
          if (!LEAVING_KINDS.has(kind)) ctx.openEntity(kind, id, options);
        },
      },
    [ctx],
  );
  if (!value) return <>{children}</>;
  return <EntityDetailContext.Provider value={value}>{children}</EntityDetailContext.Provider>;
}

export function useEntityDetailOptional(): EntityDetailContextValue | undefined {
  return useAnnouncedEntityDetail(useContext(EntityDetailContext)) ?? undefined;
}

/**
 * Closes this overlay when an entity detail is about to present somewhere a
 * modal would bury: the profile island and the team page (see
 * `registerDetailYield`). Call it from any modal overlay that renders
 * interactive entity chips, with the overlay's open state and close handler.
 *
 * This is the systemic half of the app's leave-to-entity convention — global
 * search and the automation-runs dialog close themselves before opening an
 * issue; entity CHIP presses route through `openEntity` without the host in
 * the call chain, so the host registers here instead.
 */
export function useYieldToEntityDetail(open: boolean, onClose: () => void): void {
  const register = useEntityDetailOptional()?.registerDetailYield;
  // Latest-close ref: an unstable onClose must not churn the registration.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });
  useEffect(() => {
    if (!open || !register) return;
    return register(() => onCloseRef.current());
  }, [open, register]);
}
